import { useEffect, useRef } from "react";

/**
 * GoldGlobe — a lightweight, dependency-free 3D wireframe globe rendered on a
 * 2D canvas. Gold lines on transparent background, auto-rotating and
 * scroll-reactive. Respects prefers-reduced-motion.
 */
export const GoldGlobe = ({ className = "" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    // --- Build geometry (unit sphere) ---
    const N = 60;
    const rings = [];
    // Latitude rings
    for (const lat of [-60, -40, -20, 0, 20, 40, 60]) {
      const la = (lat * Math.PI) / 180;
      const r = Math.cos(la);
      const y = Math.sin(la);
      const pts = [];
      for (let i = 0; i <= N; i++) {
        const t = (i / N) * Math.PI * 2;
        pts.push([Math.cos(t) * r, y, Math.sin(t) * r]);
      }
      rings.push(pts);
    }
    // Meridian rings
    for (const lon of [0, 30, 60, 90, 120, 150]) {
      const lo = (lon * Math.PI) / 180;
      const pts = [];
      for (let i = 0; i <= N; i++) {
        const t = (i / N) * Math.PI * 2;
        pts.push([Math.cos(t) * Math.cos(lo), Math.sin(t), -Math.cos(t) * Math.sin(lo)]);
      }
      rings.push(pts);
    }
    // Glowing nodes (fibonacci sphere)
    const nodes = [];
    const NC = 26;
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < NC; i++) {
      const y = 1 - (i / (NC - 1)) * 2;
      const r = Math.sqrt(1 - y * y);
      const th = golden * i;
      nodes.push([Math.cos(th) * r, y, Math.sin(th) * r]);
    }

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const tilt = -0.42;
    const cosT = Math.cos(tilt);
    const sinT = Math.sin(tilt);

    const project = (p, ry, R, cx, cy) => {
      // rotate around Y
      const cosY = Math.cos(ry);
      const sinY = Math.sin(ry);
      let x = p[0] * cosY + p[2] * sinY;
      let z = -p[0] * sinY + p[2] * cosY;
      let y = p[1];
      // rotate around X (tilt)
      const y2 = y * cosT - z * sinT;
      const z2 = y * sinT + z * cosT;
      const persp = 1 + z2 * 0.22;
      return { x: cx + x * R * persp, y: cy + y2 * R * persp, z: z2 };
    };

    let raf;
    const render = (ry) => {
      ctx.clearRect(0, 0, width, height);
      const cx = width / 2;
      const cy = height / 2;
      const R = Math.min(width, height) * 0.42;

      // rings
      for (const ring of rings) {
        for (let i = 0; i < ring.length - 1; i++) {
          const a = project(ring[i], ry, R, cx, cy);
          const b = project(ring[i + 1], ry, R, cx, cy);
          const zAvg = (a.z + b.z) / 2; // -1..1
          const alpha = 0.10 + ((zAvg + 1) / 2) * 0.42;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(194,152,74,${alpha.toFixed(3)})`;
          ctx.lineWidth = 0.6 + ((zAvg + 1) / 2) * 0.8;
          ctx.stroke();
        }
      }

      // nodes
      for (const n of nodes) {
        const p = project(n, ry, R, cx, cy);
        const front = (p.z + 1) / 2;
        const rad = 1.1 + front * 2.2;
        const alpha = 0.25 + front * 0.7;
        ctx.beginPath();
        ctx.arc(p.x, p.y, rad, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(224,190,108,${alpha.toFixed(3)})`;
        ctx.shadowColor = "rgba(224,190,108,0.8)";
        ctx.shadowBlur = front * 10;
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    };

    if (reduced) {
      render(0.6);
      window.removeEventListener("resize", resize);
      const ro = () => { resize(); render(0.6); };
      window.addEventListener("resize", ro);
      return () => window.removeEventListener("resize", ro);
    }

    const start = performance.now();
    const loop = (now) => {
      const t = (now - start) / 1000;
      const scroll = window.scrollY || 0;
      const ry = t * 0.18 + scroll * 0.0018;
      render(ry);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
      data-testid="gold-globe"
    />
  );
};
